import { FC, useState, useEffect } from 'react';
import { faShareNodes } from '@fortawesome/pro-regular-svg-icons';
import { BoxV2 as Box, Flex, Button, usePermission } from 'portal-commons';

import CarrierStatusTable, { type Carrier } from './CarrierStatusTable';
import RcsSectionBlock from './RcsSectionBlock';
import RcsCampaignActionModal from '../RcsCampaignActionModal';
import { campaignController, enumController } from '@/apis/core';
import { getDcaOptions, getRejectionCategoryOptions } from '@/apis/services';
import { CampaignOperation, CampaignDetail } from '@/apis/core/types';
import { useAuthContext } from '@/contexts';
import { sortMnoList } from '@/utils/index';
import MnoStatusActionModal from './MnoStatusActionModal';
import { RcsCarrierAction } from './types';
import { SelectItem } from '@/types';

interface CarrierStatusSectionProps {
  campaign?: CampaignDetail;
  onAcceptElection?: () => void;
  onDeclineElection?: () => void;
}

type CampaignActionModal = 'acceptCampaign' | 'declineCampaign';

const CarrierStatusSection: FC<CarrierStatusSectionProps> = ({
  campaign,
  onAcceptElection,
  onDeclineElection,
}) => {
  const [loading, setLoading] = useState(false);
  const { profile } = useAuthContext();
  const [carriers, setCarriers] = useState<Carrier[]>([]);
  const { isPrimaryDca, sharingStatus, secondarySharingStatus } =
    campaign || {};
  const computeShowNominationPopup = () =>
    !!isPrimaryDca
      ? sharingStatus === 'PENDING'
      : secondarySharingStatus === 'PENDING';

  const [showNominationPopup, setShowNominationPopup] = useState(false);
  const [campaignActionModal, setCampaignActionModal] =
    useState<CampaignActionModal | null>(null);
  const [mnoActionModal, setMnoActionModal] = useState<RcsCarrierAction | null>(
    null
  );
  const [activeMno, setActiveMno] = useState<{
    mnoId: number;
    mno: string;
  } | null>(null);

  const [dcaOptions, setDcaOptions] = useState<
    Record<string, { label: string; value: string; disabled?: boolean }[]>
  >({});

  const [rejectionCategoryOptions, setRejectionCategoryOptions] = useState<
    SelectItem[]
  >([]);

  const [suspensionCategoryOptions, setSuspensionCategoryOptions] = useState<
    SelectItem[]
  >([]);
  const dcaElectionList = activeMno ? dcaOptions[activeMno.mnoId] || [] : [];

  const { hasPermission } = usePermission();

  useEffect(() => {
    if (showNominationPopup && rejectionCategoryOptions?.length < 1) {
      getRejectionCategoryOptions().then((options) =>
        setRejectionCategoryOptions(options)
      );
    }
  }, [showNominationPopup]);

  useEffect(() => {
    setShowNominationPopup(computeShowNominationPopup());
  }, [campaign]);

  useEffect(() => {
    if (campaign) {
      fetchCampaignOperations(campaign?.uid);

      if (campaign?.status !== 'EXPIRED') {
        enumController
          .getSuspensionCategories()
          .then((categories) =>
            categories.map((category) => ({
              label: category.name,
              value: category.id,
            }))
          )
          .then((options) => setSuspensionCategoryOptions(options));
      }
    }
  }, [campaign?.uid]);

  useEffect(() => {
    const mnoId = activeMno?.mnoId;
    if (
      mnoActionModal &&
      ['electDca', 'updateDca'].includes(mnoActionModal) &&
      mnoId &&
      !dcaOptions[mnoId]
    ) {
      fetchDcaOptions(mnoId);
    }
  }, [mnoActionModal]);

  const isDirectCarrier = (operation: CampaignOperation) => {
    return operation.dcas.some((dca) => dca.id === profile?.uid);
  };

  const findLastDca = (operation: CampaignOperation) => {
    if (!Array.isArray(operation.dcas) || operation.dcas.length === 0) {
      return null;
    }
    return operation.dcas.reduce(
      (latest, current) =>
        current.createDate > latest.createDate ? current : latest,
      operation.dcas[0]
    );
  };

  const fetchCampaignOperations = async (campaignUid?: string) => {
    if (!campaignUid) return;

    setLoading(true);
    const data = await campaignController.getOperations(campaignUid);
    const verifications = campaign?.googleAgentId
      ? await campaignController.getVerification(
          campaignUid,
          { agentId: campaign?.googleAgentId || '' },
          { silent: true }
        )
      : [];

    const operationCarriers = sortMnoList(data, [
      'rulesEngineResultsJson',
      'mno',
    ]).map((d) => {
      const lastDca = findLastDca(d);
      const verification = verifications.find(
        (v) => Array.isArray(v.mnoIds) && v.mnoIds.includes(d.networkId)
      );

      return {
        mnoId: d.networkId,
        mno: d.rulesEngineResultsJson?.mno as string,
        qualifying: !!d.rulesEngineResultsJson?.qualify as boolean,
        mnoReview:
          typeof d.rulesEngineResultsJson?.mnoReview === 'boolean'
            ? d.rulesEngineResultsJson.mnoReview
            : null,
        campaignStatus: d.status,
        sharingStatus: lastDca?.status,
        electedDca: lastDca?.name,
        verification: {
          status: verification?.verificationStatus || '',
          vettingId: verification?.vettingId,
          vettingToken: verification?.vettingToken,
          reasons: verification?.reasons || [],
        },
        directCarrier: isDirectCarrier(d),
      };
    });
    setCarriers(operationCarriers as Carrier[]);
    setLoading(false);
  };

  const fetchDcaOptions = async (mnoId: number) => {
    const options = await getDcaOptions({
      mnoId,
      acceptCampaignShare: true,
      channelId: 'RCS',
    });
    const EmptyDcaVirtualItem = [
      {
        label:
          'Based on the use-case there is no Secondary DCA eligible for selection. Contact your CNP for details.',
        value: '0',
        disabled: true,
      },
    ];
    setDcaOptions({
      [mnoId]: options.length > 0 ? options : EmptyDcaVirtualItem,
    });
  };

  const handleCampaignActionComplete = () => {
    if (!campaign) return;

    if (campaignActionModal === 'acceptCampaign') {
      fetchCampaignOperations(campaign.uid);
      setCampaignActionModal(null);
      setShowNominationPopup(false);
      onAcceptElection?.();
    } else if (campaignActionModal === 'declineCampaign') {
      setCampaignActionModal(null);
      onDeclineElection?.();
    }
  };

  const handleCarrierTableAction = async (
    mnoInfo: { mnoId: number; mno: string },
    action: RcsCarrierAction
  ) => {
    setActiveMno(mnoInfo);
    setMnoActionModal(action);
  };

  const handleRcsStatusActionComplete = () => {
    fetchCampaignOperations(campaign?.uid);
    setMnoActionModal(null);
  };

  const handleAcceptNomination = () => {
    setCampaignActionModal('acceptCampaign');
  };

  return (
    <RcsSectionBlock
      icon={faShareNodes}
      title="Carrier Status"
      showDivider={false}
      sx={{
        position: 'relative',
        height: !loading && showNominationPopup ? '350px' : 'auto',
      }}
    >
      {loading ? (
        <div role="status" aria-live="polite">
          Loading...
        </div>
      ) : !showNominationPopup ? (
        <CarrierStatusTable
          data={carriers}
          onActionClick={handleCarrierTableAction}
          dca2={!isPrimaryDca}
          campaignExpired={campaign?.status === 'EXPIRED'}
        />
      ) : (
        <Box
          sx={{
            position: 'absolute',
            background: 'rgba(0, 0, 0, 0.50)',
            backdropFilter: 'blur(1px)',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            lineHeight: 'normal',
          }}
        >
          <Flex
            data-testid="cnpNominationPopup"
            sx={{
              flexDirection: 'column',
              alignItems: 'center',
              background: '#fff',
              color: 't.black80',
              fontSize: 'H300',
              p: 'm',
              borderRadius: '4px',
              width: '525px',
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              px: 'xxl',
              pt: 'xl',
              pb: 's',
            }}
          >
            <Box as="p" sx={{ textAlign: 'center' }}>
              You have been elected as a <strong>Connectivity Partner</strong>{' '}
              for this campaign.
              {isPrimaryDca && (
                <>
                  <br />
                  If you accept, please remember to assign a{' '}
                  <strong>DCA for each MNO</strong>.
                </>
              )}
            </Box>
            <Flex sx={{ gap: 'm', my: '30px' }}>
              <Button
                variant="outline"
                color="primary"
                onClick={() => setCampaignActionModal('declineCampaign')}
                disabled={
                  !hasPermission('rcsCampaignDetail.declineConnectivityPartner')
                }
              >
                Decline
              </Button>
              <Button
                variant="standard"
                color="primary"
                onClick={handleAcceptNomination}
                disabled={
                  !hasPermission('rcsCampaignDetail.acceptConnectivityPartner')
                }
              >
                Accept
              </Button>
            </Flex>
          </Flex>
        </Box>
      )}
      <RcsCampaignActionModal
        campaignUid={campaign?.uid}
        type={campaignActionModal === 'declineCampaign' ? 'decline' : 'accept'}
        open={!!campaignActionModal}
        decliningCampaign={{
          brandUid: campaign?.brandUid || '',
          campaignUid: campaign?.uid || '',
          downstreamCnpName:
            (isPrimaryDca
              ? campaign?.downstreamCnpName
              : campaign?.primaryDcaName) || '',
        }}
        rejectionCategoryOptions={rejectionCategoryOptions}
        onCancel={() => setCampaignActionModal(null)}
        onComplete={handleCampaignActionComplete}
        dca2={!isPrimaryDca}
      />
      <MnoStatusActionModal
        onCancel={() => setMnoActionModal(null)}
        onComplete={handleRcsStatusActionComplete}
        open={!!mnoActionModal}
        action={mnoActionModal}
        campaign={campaign}
        dcaOptions={dcaElectionList}
        suspensionCategoryOptions={suspensionCategoryOptions}
        carrier={activeMno || { mnoId: -1, mno: '' }}
      />
    </RcsSectionBlock>
  );
};

export default CarrierStatusSection;
